import React, { useCallback, useContext, useEffect, useState } from 'react';
import GridLayout from 'react-grid-layout';
import { Pencil, Save, LayoutGrid } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../assets/styles/dashboard-tokens.css';
import '../assets/styles/Dashboard.css';
import 'react-grid-layout/css/styles.css';
import LoggedInHeader from '../components/LoggedInHeader';
import Graph from '../components/Graph';
import AddChartsDrawer from '../components/AddChartsDrawer';
import AuthLogin from '../profile/auth-login';
import TermsAndConditionsPopup from '../components/TermsAndConditionsPopup';
import TooltipWrapper from '../components/ToolTipWrapper';
import LeaveConfirmationPopup from '../components/LeaveConfirmationPopup';
import DashboardTemplateTabs from '../components/DashboardTemplateTabs';
import DashboardTemplateView from '../components/DashboardTemplateView';
import useIsMobile from '../hooks/useIsMobile';
import {
  GRID_ROW_HEIGHT,
  DASHBOARD_GRID_STATIC_PROPS,
  getWatermarkSizeTier
} from '../components/dashboardGridConfig';

// Charts whose underlying data is a genuine multi-year calendar time series
// (as opposed to e.g. the YTD/MTD charts, which plot averages computed across
// all historical years and have no meaningful "last 1 year" slice) - only
// these get a size-driven data-range tier passed to their iframe.
const RANGE_AWARE_CHART_URL_PATTERNS = [/^\/rsiSignals\//, /^\/marketBreadth\/spx-above-200ema\//];

const isRangeAwareChart = (url) =>
  typeof url === 'string' && RANGE_AWARE_CHART_URL_PATTERNS.some((pattern) => pattern.test(url));

/**
 * Maps a chart's grid height (in row units, out of the 1-10 minH/maxH range)
 * to a trailing data-range tier. Tunable constants - not a hard requirement.
 *
 * @param {number} h - Grid row units for the chart's current layout item
 * @returns {'1y'|'3y'|'all'}
 */
const getDataRangeForSize = (h) => {
  if (h <= 2) return '1y';
  if (h <= 5) return '3y';
  return 'all';
};

/**
 * Dashboard Component
 * 
 * Main dashboard view that displays user's customizable chart grid.
 * Features include:
 * - Dynamic grid layout with drag-and-drop capability
 * - Edit mode for repositioning and resizing charts
 * - Layout persistence to backend
 * - Unsaved changes detection
 * - Navigation handling with confirmation for unsaved changes
 */
const Dashboard = () => {
  // Authentication context for user information and state
  const ctx = useContext(AuthLogin);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();

  // Core state management
  const [charts, setCharts] = useState([]); // Stores the chart data fetched from API
  const [isEditMode, setIsEditMode] = useState(false); // Tracks if user is in layout editing mode
  const [layout, setLayout] = useState([]); // Stores the grid layout configuration
  // Mirrors `layout`, but only updates on initial load, chart add, or resize-stop
  // (never mid-drag) - used to decide each chart's data-range tier so the
  // embedded iframe doesn't reload on every drag tick, only once resizing settles
  const [committedLayout, setCommittedLayout] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth - 48); // Tracks viewport width for responsive grid
  const [dynamicHeight, setDynamicHeight] = useState(600); // Dynamic height for grid container
  const [hasFetchedDashboard, setHasFetchedDashboard] = useState(false); // True once the initial getUserDashboard fetch resolves (success or failure) - avoids flashing the empty state before data arrives

  // Unsaved changes management
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // Tracks if layout changes are unsaved
  const [showLeaveConfirmation, setShowLeaveConfirmation] = useState(false); // Controls leave confirmation popup
  const [pendingNavigation, setPendingNavigation] = useState(null); // Stores navigation path when confirmation is needed

  // Add Charts drawer state - opened via the "+ Add Charts" toolbar button,
  // the header nav item (see onAddChartsClick below), or a ?addCharts=1
  // redirect from the old /add-charts route (see routes.js)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Dashboard templates (preset dashboards) - the tab strip under the
  // header. 'custom' is the user's own editable dashboard above; any other
  // value is a template slug from GET /getDashboardTemplates, rendered
  // read-only. Selection is mirrored to the ?view= search param.
  const [templates, setTemplates] = useState([]);
  const [activeView, setActiveView] = useState('custom');
  const [pendingTabChange, setPendingTabChange] = useState(null); // Parallels pendingNavigation below
  const [isCopyingTemplate, setIsCopyingTemplate] = useState(false);

  /**
   * Auto-open the Add Charts drawer when redirected here from the old
   * /add-charts route (routes.js redirects to /my-dashboard?addCharts=1),
   * then clean the query param off the URL.
   */
  useEffect(() => {
    if (searchParams.get('addCharts') === '1') {
      setIsDrawerOpen(true);
      setSearchParams({}, { replace: true });
    }
    // Mount-only: intentionally does not depend on searchParams/setSearchParams
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Fetch all preset dashboards (templates) once on mount. Independent
   * failure domain from the getUserDashboard fetch below - a failure here
   * just leaves the tab strip showing only "My Dashboard".
   */
  useEffect(() => {
    fetch(`${FINNTECH_API_URL}/getDashboardTemplates`)
      .then((r) => r.json())
      .then(setTemplates)
      .catch((error) => console.error('Error fetching dashboard templates:', error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Keeps the active tab in sync with the ?view=<slug> param (no param means
   * 'custom'). Unlike ?addCharts=1 above this is NOT stripped after read -
   * it stays in the URL so the view is shareable/bookmarkable, and re-running
   * on searchParams changes makes browser Back/Forward switch tabs. Also
   * re-checked once templates finish loading, since a template slug isn't
   * valid until that fetch resolves.
   */
  useEffect(() => {
    const validSlugs = new Set(['custom', ...templates.map((t) => t.slug)]);
    const requested = searchParams.get('view') || 'custom';
    if (validSlugs.has(requested)) {
      setActiveView(requested);
    }
  }, [templates, searchParams]);

  /**
   * Window resize handler
   * Updates grid width when window is resized to maintain responsive layout
   */
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth - 48); // Subtracting padding
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /**
   * Unsaved changes detection
   * Warns user when attempting to leave page with unsaved changes
   */
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isEditMode && hasUnsavedChanges) {
        // Standard way to show browser's confirm dialog
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
        return ''; // This message isn't shown in modern browsers
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isEditMode, hasUnsavedChanges]);

  /**
   * Mobile is view-only (wireframe: single-column stack, no edit mode).
   * Entering the mobile breakpoint mid-edit discards any in-progress
   * unsaved layout so the beforeunload/leave-confirmation guards go quiet.
   */
  useEffect(() => {
    if (isMobile) {
      setIsEditMode(false);
      setHasUnsavedChanges(false);
    }
  }, [isMobile]);

  // API endpoint from environment variables
  const FINNTECH_API_URL = process.env.REACT_APP_API_URL;

  /**
   * Strips per-item interaction flags from layout items, keeping geometry only.
   * react-grid-layout gives an explicit boolean `isDraggable`/`isResizable` on
   * a layout item precedence over the grid-level isDraggable/isResizable
   * ={isEditMode} props, so any such flag reaching state (or the backend, which
   * round-trips the array) would make charts movable outside edit mode. The
   * grid-level props are the single source of truth for interactivity.
   *
   * @param {Array} items - Layout items from react-grid-layout or the backend
   * @returns {Array} Items with only geometry/constraint fields
   */
  const sanitizeLayoutItems = (items) =>
    items.map(({ i, x, y, w, h, minW, maxW, minH, maxH }) => ({
      i, x, y, w, h, minW, maxW, minH, maxH,
      static: false
    }));

  /**
   * Validate layout to ensure proper spacing when dragging items
   * Prevents items from being placed too close to the bottom edge
   * 
   * @param {Array} newLayout - New layout after drag/resize operation
   * @returns {Array} Validated layout with proper spacing
   */
  const validateLayoutSpacing = (newLayout) => {
    if (!newLayout || newLayout.length === 0) {
      return newLayout;
    }

    // Find the maximum Y position in the layout
    let maxY = 0;
    newLayout.forEach(item => {
      const bottomY = item.y + item.h;
      if (bottomY > maxY) {
        maxY = bottomY;
      }
    });

    // Ensure there's always space below the bottommost item
    // The CSS handles the visual spacing, this is for layout validation
    return sanitizeLayoutItems(newLayout);
  };

  /**
   * Fetch user's dashboard configuration
   * Retrieves chart data and layout from API on component mount
   */
  useEffect(() => {
    const fetchCharts = async () => {
      try {
        const url = `${FINNTECH_API_URL}/getUserDashboard/${ctx.userId}`;
        const response = await fetch(url);
        const data = await response.json();

        if (data.charts && data.layouts) {
          // Sanitize on ingest: saved layouts may carry stale per-item
          // isDraggable/isResizable flags that would override edit-mode gating
          const cleanLayouts = sanitizeLayoutItems(data.layouts);
          setCharts(data.charts);
          setLayout(cleanLayouts);
          setCommittedLayout(cleanLayouts);
        } else {
          // Handle case where user has no dashboard yet
          setCharts([]);
          setLayout([]);
          setCommittedLayout([]);
        }
      } catch (error) {
        console.error('Error fetching dashboard:', error);
        setCharts([]);
        setLayout([]);
        setCommittedLayout([]);
      } finally {
        setHasFetchedDashboard(true);
      }
    };
    fetchCharts();
  }, [ctx.userId, FINNTECH_API_URL]);

  /**
   * Removes a chart from the dashboard
   * Updates both local state and persists removal to backend
   * 
   * @param {number} chartId - ID of the chart to remove
   */
  const handleRemoveFromDashboard = async (chartId) => {
    if (!chartId) {
      console.error('handleRemoveFromDashboard: chartId is required');
      return;
    }

    try {
      // Update local state immediately for responsive UI
      setCharts(prevCharts => prevCharts.filter(chart => chart.id !== chartId));
      setLayout(prevLayout => prevLayout.filter(item => item.i !== chartId.toString()));
      setHasUnsavedChanges(true);
      
      const requestBody = {
        "chartIdToRemove": chartId
      };

      // Persist changes to backend
      const url = `${process.env.REACT_APP_API_URL}/deleteFromDashboard/${ctx.userId}`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      const responseText = await response.text();
      
      if (!response.ok) {
        throw new Error(`Failed to update dashboard: ${responseText}`);
      }
    } catch (error) {
      console.error('Error updating dashboard:', error);
      alert(`Failed to remove chart: ${error.message}`);
      
      // Attempt to refresh dashboard data if update fails
      try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}/getUserDashboard/${ctx.userId}`);
        const data = await fetchResponse.json();
        const cleanLayouts = sanitizeLayoutItems(data.layouts || []);
        setCharts(data.charts || []);
        setLayout(cleanLayouts);
        setCommittedLayout(cleanLayouts);
      } catch (refreshError) {
        console.error('Error refreshing dashboard:', refreshError);
      }
    }
  };

  /**
   * Computes a grid position for a newly-added chart that cannot overlap any
   * existing chart, regardless of how charts have been dragged/resized in
   * edit mode: it places the new item at x=0, directly below the bottommost
   * edge of every current layout item (same max-bottom-row math as
   * calculateDynamicHeight).
   *
   * @param {Array} currentLayout - Current layout configuration
   * @returns {{x: number, y: number, w: number, h: number}}
   */
  const getNextAvailablePosition = (currentLayout) => {
    let maxBottomY = 0;
    currentLayout.forEach(item => {
      const bottomY = item.y + item.h;
      if (bottomY > maxBottomY) {
        maxBottomY = bottomY;
      }
    });
    return { x: 0, y: maxBottomY, w: 6, h: 2 };
  };

  /**
   * Handles a chart being added from the Add Charts drawer.
   * Appends the chart + a non-overlapping grid position to local state
   * directly (no refetch/reorder) so it appears immediately without
   * disturbing any already-mounted chart iframes, then fires a background
   * PUT /saveLayout to correct the naive {x:0,y:0} position the
   * /addToDashboard endpoint assigns server-side by default.
   *
   * This intentionally does not touch isEditMode/hasUnsavedChanges/the Save
   * Layout button - chart additions persist immediately, same as removals.
   *
   * @param {Object} chart - The chart object returned by AddChartsDrawer
   */
  const handleChartAdded = async (chart) => {
    const position = getNextAvailablePosition(layout);
    const newLayoutItem = {
      i: chart.id.toString(),
      x: position.x,
      y: position.y,
      w: position.w,
      h: position.h,
      minW: 2,
      maxW: 24,
      minH: 1,
      maxH: 10,
      static: false
    };

    const updatedLayout = [...layout, newLayoutItem];
    setCharts(prevCharts => [...prevCharts, chart]);
    setLayout(updatedLayout);
    setCommittedLayout(updatedLayout);

    try {
      const url = `${FINNTECH_API_URL}/saveLayout/${ctx.userId}`;
      await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ layouts: sanitizeLayoutItems(updatedLayout) })
      });
    } catch (error) {
      console.error('Error persisting new chart position:', error);
    }
  };

  /**
   * Saves the current layout configuration to the backend
   * Called when user clicks Save Layout button
   * Also handles pending navigation if present
   */
  const handleSaveLayout = async () => {
    try {
      // console.log('Current layout state:', layout); // Debug log
  
      const layoutData = {
        layouts: sanitizeLayoutItems(layout)
      };
      
      // console.log('Sending layout data:', layoutData); // Debug log
      
      const url = `${FINNTECH_API_URL}/saveLayout/${ctx.userId}`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(layoutData)
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save layout');
      }
  
      await response.json();
  
      // Reset edit state
      setIsEditMode(false);
      setHasUnsavedChanges(false);
      
      // Handle pending navigation or pending tab change if either exists
      // (a click can only ever have set one of the two)
      if (pendingNavigation) {
        navigate(pendingNavigation);
        setPendingNavigation(null);
      }
      if (pendingTabChange) {
        switchToView(pendingTabChange);
        setPendingTabChange(null);
      }

      // Close the confirmation popup if it was open
      setShowLeaveConfirmation(false);
    } catch (error) {
      console.error('Error saving layout:', error);
      alert('Failed to save layout configuration');
    }
  };

  /**
   * Logs user acceptance of Terms and Conditions to backend
   * 
   * @param {string} user_id - User ID to log acceptance for
   * @returns {Promise} API response JSON
   */
  const logUserAcceptedTermsAndConditions = useCallback(async (user_id) => {
    const url = `${FINNTECH_API_URL}/logUserSignedTermsAndConditions/${user_id}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error();
    }

    return await response.json();
  }, [FINNTECH_API_URL]);

  /**
   * Handles user acceptance of Terms and Conditions
   * Updates local storage and context state
   */
  const handleTermsAndConditionsAccept = async (event) => {
    await logUserAcceptedTermsAndConditions(ctx.userId);
    const userData = JSON.parse(localStorage.getItem('userInfo') || '[]');
    
    // Update user data in local storage
    const updatedUserData = {
      ...userData,
      firstTimeLogin: "false",
      userAcceptedTermsAndConditions: "true"
    };
    
    localStorage.setItem('userInfo', JSON.stringify(updatedUserData));
    ctx.setFirstTimeLogin(false);
    ctx.setuserAcceptedTermsAndConditions(true);
  };

  /**
   * Calculate dynamic height based on current layout
   * Ensures there's always space below the lowest chart
   * 
   * @param {Array} currentLayout - Current layout configuration
   * @returns {number} Calculated height in pixels
   */
  const calculateDynamicHeight = useCallback((currentLayout) => {
    if (!currentLayout || currentLayout.length === 0) {
      return 600; // Minimum height
    }
    
    let maxBottomY = 0;
    currentLayout.forEach(item => {
      const bottomY = (item.y + item.h) * GRID_ROW_HEIGHT; // Convert grid units to pixels
      if (bottomY > maxBottomY) {
        maxBottomY = bottomY;
      }
    });
    
    // Add buffer space (at least 300px = 2 grid units worth of space)
    return Math.max(600, maxBottomY + 300);
  }, []);

  /**
   * Update dynamic height when layout changes
   */
  useEffect(() => {
    const newHeight = calculateDynamicHeight(layout);
    setDynamicHeight(newHeight);
  }, [layout, calculateDynamicHeight]);

  /**
   * Handles grid layout changes
   * Updates layout state and tracks unsaved changes with validation
   * 
   * @param {Array} newLayout - New layout configuration from react-grid-layout
   */
  const onLayoutChange = useCallback((newLayout) => {
    // Validate the layout before setting it
    const validatedLayout = validateLayoutSpacing(newLayout);
    setLayout(validatedLayout);

    // Update dynamic height in real-time during editing
    if (isEditMode) {
      const newHeight = calculateDynamicHeight(validatedLayout);
      setDynamicHeight(newHeight);
      setHasUnsavedChanges(true);
    }
  }, [isEditMode, calculateDynamicHeight]);

  /**
   * Commits the layout to `committedLayout` once a resize interaction ends.
   * This (not `onLayoutChange`, which fires continuously mid-drag) is what
   * each chart's data-range tier is derived from, so range-aware charts
   * (RSI signals, SPX breadth) only reload with a new date window once the
   * user releases the resize handle, not on every pixel of dragging.
   */
  const onResizeStop = useCallback((newLayout) => {
    setCommittedLayout(validateLayoutSpacing(newLayout));
  }, []);

  /**
   * Switches the active tab (custom dashboard or a template slug) and
   * mirrors the selection into the ?view= search param.
   *
   * @param {string} slug - 'custom' or a template slug
   */
  const switchToView = (slug) => {
    setActiveView(slug);
    setSearchParams(slug === 'custom' ? {} : { view: slug }, { replace: false });
  };

  /**
   * Tab strip click handler. Reuses the same unsaved-changes guard as
   * regular navigation (handleNavigation below) - if there's an unsaved
   * layout edit in progress, defer the tab switch behind the leave
   * confirmation popup instead of switching immediately.
   *
   * @param {string} slug - 'custom' or a template slug
   */
  const handleTabClick = (slug) => {
    if (isEditMode && hasUnsavedChanges) {
      setPendingTabChange(slug);
      setShowLeaveConfirmation(true);
    } else {
      switchToView(slug);
    }
  };

  /**
   * Copies a template's charts onto the user's own dashboard with a single
   * /saveLayout call. Dashboard membership is just the saved layouts array,
   * so appending the template's relative layout (shifted below the user's
   * existing content - same principle as getNextAvailablePosition above)
   * both adds and positions the charts. Per-chart /addToDashboard calls are
   * deliberately avoided: run in parallel they race on the dashboards row.
   * Local state only updates once the save succeeds. Finishes by switching
   * back to 'custom'.
   *
   * @param {Object} template - {slug, name, charts, layouts} from GET /getDashboardTemplates
   */
  const handleCopyToDashboard = async (template) => {
    const existingIds = new Set(charts.map((c) => c.id));
    const chartsToAdd = template.charts.filter((c) => !existingIds.has(c.id));
    if (chartsToAdd.length === 0) return; // Defensive - the button is already disabled in this state

    setIsCopyingTemplate(true);
    try {
      const shiftY = layout.reduce((max, item) => Math.max(max, item.y + item.h), 0);
      const newLayoutItems = template.layouts
        .filter((item) => !existingIds.has(parseInt(item.i, 10)))
        .map((item) => ({ ...item, y: item.y + shiftY }));

      const mergedLayout = [...layout, ...newLayoutItems];
      const mergedCharts = [...charts, ...chartsToAdd];

      const url = `${FINNTECH_API_URL}/saveLayout/${ctx.userId}`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ layouts: sanitizeLayoutItems(mergedLayout) })
      });
      if (!response.ok) {
        throw new Error(`Failed to save layout (${response.status})`);
      }

      setCharts(mergedCharts);
      setLayout(mergedLayout);
      setCommittedLayout(mergedLayout);
      switchToView('custom');
    } catch (error) {
      console.error('Error copying template to dashboard:', error);
      alert('Failed to copy template to your dashboard');

      // Resync from the server on failure, same recovery pattern as handleRemoveFromDashboard
      try {
        const fetchResponse = await fetch(`${FINNTECH_API_URL}/getUserDashboard/${ctx.userId}`);
        const data = await fetchResponse.json();
        const cleanLayouts = sanitizeLayoutItems(data.layouts || []);
        setCharts(data.charts || []);
        setLayout(cleanLayouts);
        setCommittedLayout(cleanLayouts);
      } catch (refreshError) {
        console.error('Error refreshing dashboard:', refreshError);
      }
    } finally {
      setIsCopyingTemplate(false);
    }
  };

  /**
   * Smart navigation handler
   * Checks for unsaved changes before navigation and shows confirmation if needed
   *
   * @param {string} path - Target navigation path
   */
  const handleNavigation = (path) => {
    if (isEditMode && hasUnsavedChanges) {
      // Store pending navigation and show confirmation
      setPendingNavigation(path);
      setShowLeaveConfirmation(true);
    } else {
      // Direct navigation if no unsaved changes
      navigate(path);
    }
  };
  
  /**
   * Handles navigation without saving changes
   * Called when user confirms they want to leave without saving
   */
  const handleLeaveWithoutSaving = () => {
    setIsEditMode(false);
    setHasUnsavedChanges(false);
    setShowLeaveConfirmation(false);

    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
    if (pendingTabChange) {
      switchToView(pendingTabChange);
      setPendingTabChange(null);
    }
  };

  /**
   * Closes the confirmation popup without taking action
   * Cancels pending navigation or pending tab change
   */
  const handleCloseConfirmation = () => {
    setShowLeaveConfirmation(false);
    setPendingNavigation(null);
    setPendingTabChange(null);
  };

  return (
    <>
      {/* Show Terms & Conditions for first-time users */}
      {(!ctx.userAcceptedTermsAndConditions) ? (
        <TermsAndConditionsPopup onAccept={handleTermsAndConditionsAccept} />
      ) : (
        <>
          {/* Use LoggedInHeader with unsaved changes support */}
          <LoggedInHeader
            isEditMode={isEditMode}
            hasUnsavedChanges={hasUnsavedChanges}
            onNavigationRequest={handleNavigation}
            setPendingNavigation={setPendingNavigation}
            setShowLeaveConfirmation={setShowLeaveConfirmation}
            onAddChartsClick={() => setIsDrawerOpen(true)}
          />
          <DashboardTemplateTabs
            templates={templates}
            activeView={activeView}
            onSelect={handleTabClick}
          />
          <div className="dashboard">
            {activeView === 'custom' ? (
              <>
                {/* Edit mode controls - hidden on mobile (view-only; Add Charts
                    lives in the hamburger menu, which opens the drawer in place) */}
                {!isMobile && (
                <div className="dashboard-controls">
                  {isEditMode ? (
                    <button
                      className={`control-button save-button ${hasUnsavedChanges ? 'pulsing' : ''}`}
                      onClick={handleSaveLayout}
                    >
                      <Save className="control-icon" />
                      Save Layout
                    </button>
                  ) : (
                    <button
                      className="control-button edit-button"
                      onClick={() => setIsEditMode(true)}
                    >
                      <Pencil className="control-icon" />
                      Edit Layout
                    </button>
                  )}
                  {isEditMode && hasUnsavedChanges && (
                    <span className="unsaved-changes-badge">Unsaved changes</span>
                  )}
                  <button
                    className="control-button add-charts-button"
                    onClick={() => setIsDrawerOpen(true)}
                  >
                    + Add Charts
                  </button>
                </div>
                )}
                {isMobile && (
                  <h1 className="ft-dashboard-mobile-title">My Dashboard</h1>
                )}

                {/* Dynamic grid layout container, or an empty-state prompt once
                    we know for certain the user has no charts yet */}
                {hasFetchedDashboard && charts.length === 0 ? (
                  <div className="dashboard-empty-state">
                    <LayoutGrid size={56} className="dashboard-empty-state-icon" />
                    <h2>Your dashboard is empty</h2>
                    <p>Add your first chart to start building your personalized dashboard.</p>
                    <button
                      className="ft-pill-btn dashboard-empty-state-cta"
                      onClick={() => setIsDrawerOpen(true)}
                    >
                      + Add your first chart
                    </button>
                  </div>
                ) : isMobile ? (
                  /* Mobile: view-only single-column stack, ordered by the saved
                     grid layout (top-to-bottom, then left-to-right). GridLayout
                     never mounts here, so drag/resize and its touch-action:none
                     rule don't apply. Watermark/range tiers use fixed constants -
                     getWatermarkSizeTier's math assumes the 24-col desktop grid. */
                  <div className="ft-dashboard-mobile-stack">
                    {[...charts]
                      .sort((a, b) => {
                        const la = committedLayout.find((l) => l.i === a.id.toString());
                        const lb = committedLayout.find((l) => l.i === b.id.toString());
                        if (!la || !lb) return !la - !lb; // charts missing a layout entry go last
                        return (la.y - lb.y) || (la.x - lb.x);
                      })
                      .map((chart) => (
                        <div key={chart.id.toString()} className="ft-mobile-chart-card">
                          <Graph
                            {...chart}
                            showPlus={false}
                            isEditMode={false}
                            dataRange={isRangeAwareChart(chart.url) ? '1y' : undefined}
                            size="xs"
                          />
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="dashboard-grid-container" style={{ minHeight: `${dynamicHeight}px` }}>
                    <GridLayout
                      {...DASHBOARD_GRID_STATIC_PROPS}
                      className={`dashboard-grid ${isEditMode ? 'edit-mode' : ''}`}
                      layout={layout}
                      width={windowWidth}
                      isDraggable={isEditMode} // Only allow dragging in edit mode
                      isResizable={isEditMode} // Only allow resizing in edit mode
                      // Don't start a drag from the chart's trash/expand buttons - the drag
                      // placeholder would swallow the mouseup and the click would never fire
                      draggableCancel=".control-button"
                      onLayoutChange={onLayoutChange}
                      onResizeStop={onResizeStop}
                      resizeHandles={['se', 'sw', 'ne', 'nw']} // Enable resizing from all corners
                      style={{ height: `${dynamicHeight}px` }} // Set explicit height
                    >
                      {/* Map chart data to grid items */}
                      {charts.map((chart) => {
                        // Data-range tier is derived from committedLayout (updated
                        // on load/add/resize-stop only, never mid-drag) so the
                        // iframe doesn't reload on every pixel of resizing
                        const committedItem = committedLayout.find((l) => l.i === chart.id.toString());
                        const dataRange = isRangeAwareChart(chart.url) && committedItem
                          ? getDataRangeForSize(committedItem.h)
                          : undefined;
                        // Same committedItem (updates on load/add/resize-stop only)
                        // drives watermark density, so it doesn't reload mid-drag either
                        const watermarkSize = committedItem
                          ? getWatermarkSizeTier(committedItem.w, committedItem.h, windowWidth)
                          : undefined;

                        return (
                          <div key={chart.id.toString()} className="chart-wrapper">
                            <Graph
                              {...chart}
                              showPlus={false}
                              isEditMode={isEditMode} // Pass edit mode state to control remove button visibility
                              onRemove={() => handleRemoveFromDashboard(chart.id)}
                              dataRange={dataRange}
                              size={watermarkSize}
                            />
                          </div>
                        );
                      })}
                    </GridLayout>
                  </div>
                )}
              </>
            ) : (
              <DashboardTemplateView
                template={templates.find((t) => t.slug === activeView)}
                isMobile={isMobile}
                windowWidth={windowWidth}
                userChartIds={new Set(charts.map((c) => c.id))}
                onCopyToDashboard={handleCopyToDashboard}
                isCopying={isCopyingTemplate}
              />
            )}
          </div>

          {/* Add Charts drawer - overlays the dashboard instead of navigating away */}
          <AddChartsDrawer
            isOpen={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            onChartAdded={handleChartAdded}
          />

          {/* Tooltip container for chart help icons */}
          <TooltipWrapper/>
          
          {/* Leave confirmation popup - shown when attempting to navigate with unsaved changes */}
          {showLeaveConfirmation && (
            <LeaveConfirmationPopup 
              onSave={handleSaveLayout}
              onLeave={handleLeaveWithoutSaving}
              onClose={handleCloseConfirmation}
            />
          )}
        </>
      )}
    </>
  );
};

export default Dashboard;